<template>
  <div class="autocomplete-wrapper">
    <div class="autocomplete-head">
      <span class="eyebrow">
        Компания
      </span>

      <label class="hint">
        Начните вводить название компании. Если её нет в списке — можно добавить вручную.
      </label>
    </div>

    <div
      class="input-wrapper"
      :class="{
        active: !!selectedCompany,
        loading: isLoading
      }"
    >
      <template v-if="!selectedCompany">
        <div class="search-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="6"
              stroke="currentColor"
              stroke-width="1.8"
            />

            <path
              d="M16 16L20 20"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <input
          v-model="query"
          type="text"
          class="search-input"
          placeholder="Найти компанию"
          autocomplete="off"
          @input="onInput"
        />

        <div
          v-if="isLoading"
          class="input-loader"
        ></div>

        <button
          v-else-if="
            query &&
            !results.length
          "
          type="button"
          class="add-btn"
          @click="selectCustomCompany"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10 4V16M4 10H16"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          Добавить
        </button>
      </template>

      <template v-else>
        <div class="selected-display">
          <div class="selected-company">
            <div class="selected-logo">
              <img
                v-if="selectedCompany.logo_url"
                :src="selectedCompany.logo_url"
                alt=""
              />

              <span v-else>
                {{
                  selectedCompany.name
                    ?.charAt(0)
                    ?.toUpperCase() || "C"
                }}
              </span>
            </div>

            <div class="selected-info">
              <span class="selected-label">
                Выбрана компания
              </span>

              <strong>
                {{ selectedCompany.name }}
              </strong>

              <small
                v-if="
                  selectedCompany.specifics &&
                  selectedCompany.specifics !== '—'
                "
              >
                {{ selectedCompany.specifics }}
              </small>
            </div>
          </div>

          <button
            type="button"
            class="clear-btn"
            aria-label="Очистить выбор"
            @click="clearSelection"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M6 6L14 14M14 6L6 14"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </template>
    </div>

    <Transition name="results">
      <div
        v-if="
          query &&
          !selectedCompany
        "
        class="results"
      >
        <div
          v-if="isLoading"
          class="loading-state"
        >
          <div class="loader-circle"></div>

          <div>
            <strong>
              Ищем компании
            </strong>

            <span>
              Подождите немного
            </span>
          </div>
        </div>

        <template v-else-if="results.length">
          <div class="results-header">
            <span>
              Результаты
            </span>

            <span class="results-count">
              {{ results.length }}
            </span>
          </div>

          <div class="results-list">
            <button
              v-for="company in results"
              :key="company.company_id"
              type="button"
              class="company-item"
              @click="selectCompany(company)"
            >
              <div class="logo">
                <img
                  v-if="company.logo_url"
                  :src="company.logo_url"
                  alt=""
                />

                <span v-else>
                  {{
                    company.name
                      ?.charAt(0)
                      ?.toUpperCase() || "C"
                  }}
                </span>
              </div>

              <div class="info">
                <div class="name">
                  {{ company.name }}
                </div>

                <div class="spec">
                  {{
                    company.specifics ||
                    "Специализация не указана"
                  }}
                </div>
              </div>

              <div class="select-arrow">
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M7 5L12 10L7 15"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </button>
          </div>
        </template>

        <div
          v-else
          class="no-results"
        >
          <div class="empty-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="11"
                cy="11"
                r="6"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <path
                d="M16 16L20 20"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <div>
            <strong>
              Компания не найдена
            </strong>

            <span>
              Можно добавить «{{ query }}» вручную
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref } from "vue";
import debounce from "lodash/debounce";

import CompanyService
  from "../../api/CompanyService";

const emit = defineEmits([
  "update:modelValue"
]);

const query = ref("");
const results = ref([]);
const isLoading = ref(false);
const selectedCompany = ref(null);

const onInput = debounce(
  async () => {
    const normalized =
      query.value.trim();

    if (!normalized) {
      results.value = [];
      isLoading.value = false;
      return;
    }

    isLoading.value = true;

    try {
      const response =
        await CompanyService
          .searchAdminCompanies(
            normalized
          );

      results.value =
        Array.isArray(response)
          ? response
          : [];
    } catch (error) {
      console.error(
        "❌ Ошибка поиска компании:",
        error
      );

      results.value = [];
    } finally {
      isLoading.value = false;
    }
  },
  400
);

function selectCompany(
  company
) {
  selectedCompany.value =
    company;

  query.value = "";

  results.value = [];

  emit(
    "update:modelValue",
    company
  );
}

function selectCustomCompany() {
  const name =
    query.value.trim();

  if (!name) {
    return;
  }

  const customCompany = {
    company_id:
      `custom-${Date.now()}`,

    name,

    specifics: "—",

    is_custom: true
  };

  selectCompany(
    customCompany
  );
}

function clearSelection() {
  selectedCompany.value = null;

  query.value = "";

  results.value = [];

  emit(
    "update:modelValue",
    null
  );
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.autocomplete-wrapper {
  width: 100%;
  max-width: 100%;
}

/* =========================================================
   HEAD
========================================================= */

.autocomplete-head {
  margin-bottom: 10px;
}

.eyebrow {
  display: block;

  margin-bottom: 4px;

  color: #8f96a1;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  letter-spacing:
    0.08em;

  text-transform:
    uppercase;
}

.hint {
  display: block;

  margin: 0;

  color: #686f79;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;

  line-height: 1.5;
}

/* =========================================================
   INPUT
========================================================= */

.input-wrapper {
  width: 100%;
  min-height: 54px;

  display: flex;
  align-items: center;

  position: relative;

  overflow: hidden;

  background: #f6f7f9;

  border:
    1px solid
    #dfe3e8;

  border-radius: 15px;

  box-shadow:
    inset 0 1px 2px
    rgba(
      22,
      32,
      48,
      0.035
    );

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.input-wrapper:focus-within {
  background: #ffffff;

  border-color:
    #5b8ff2;

  box-shadow:
    0 0 0 3px
    rgba(
      73,
      132,
      239,
      0.12
    );
}

.input-wrapper.active {
  background:
    linear-gradient(
      135deg,
      rgba(
        53,
        132,
        247,
        0.06
      ),
      rgba(
        83,
        104,
        232,
        0.035
      )
    );

  border-color:
    rgba(
      62,
      130,
      237,
      0.24
    );
}

.search-icon {
  width: 48px;
  height: 54px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #8a919c;
}

.search-icon svg {
  width: 19px;
  height: 19px;
}

.input-wrapper:focus-within
.search-icon {
  color: #4a86eb;
}

.search-input {
  min-width: 0;
  height: 54px;

  flex: 1;

  padding:
    0
    10px
    0
    0;

  color: #232830;

  background:
    transparent;

  border: none;
  outline: none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 13px;
}

.search-input::placeholder {
  color: #a0a5ae;
}

/* =========================================================
   ADD BUTTON
========================================================= */

.add-btn {
  min-height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  flex-shrink: 0;

  margin-right: 8px;

  padding:
    0
    13px;

  cursor: pointer;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3596f8,
      #5368e7
    );

  border: none;

  border-radius: 11px;

  box-shadow:
    0 6px 14px
    rgba(
      48,
      117,
      224,
      0.2
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  transition:
    transform
    0.2s ease,
    box-shadow
    0.2s ease;
}

.add-btn svg {
  width: 14px;
  height: 14px;
}

.add-btn:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 9px 18px
    rgba(
      48,
      117,
      224,
      0.28
    );
}

/* =========================================================
   INLINE LOADER
========================================================= */

.input-loader {
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  margin-right: 16px;

  border:
    2px solid
    rgba(
      67,
      135,
      238,
      0.18
    );

  border-top-color:
    #4387ee;

  border-radius: 50%;

  animation:
    spin
    0.75s
    linear
    infinite;
}

/* =========================================================
   SELECTED
========================================================= */

.selected-display {
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;

  padding:
    8px
    9px
    8px
    10px;
}

.selected-company {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 11px;
}

.selected-logo,
.logo {
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  border-radius: 12px;

  background:
    linear-gradient(
      135deg,
      #e9f2ff,
      #ececff
    );

  color: #5277d7;

  font-family:
    "Eastman-Bold",
    sans-serif;
}

.selected-logo {
  width: 40px;
  height: 40px;

  font-size: 13px;
}

.selected-logo img,
.logo img {
  width: 100%;
  height: 100%;

  object-fit: cover;
}

.selected-info {
  min-width: 0;
}

.selected-label {
  display: block;

  margin-bottom: 1px;

  color: #9a9fa8;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  text-transform:
    uppercase;

  letter-spacing:
    0.05em;
}

.selected-info strong {
  display: block;

  overflow: hidden;

  color: #2e333b;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;

  white-space: nowrap;

  text-overflow:
    ellipsis;
}

.selected-info small {
  display: block;

  margin-top: 2px;

  overflow: hidden;

  color: #898f99;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  white-space: nowrap;

  text-overflow:
    ellipsis;
}

.clear-btn {
  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  padding: 0;

  cursor: pointer;

  color: #77808c;

  background:
    rgba(
      81,
      94,
      116,
      0.08
    );

  border: none;

  border-radius: 10px;

  transition:
    background-color
    0.2s ease,
    color
    0.2s ease,
    transform
    0.2s ease;
}

.clear-btn svg {
  width: 16px;
  height: 16px;
}

.clear-btn:hover {
  color: #d84f58;

  background:
    rgba(
      216,
      79,
      88,
      0.09
    );

  transform:
    scale(1.03);
}

/* =========================================================
   RESULTS
========================================================= */

.results {
  width: 100%;

  margin-top: 9px;

  overflow: hidden;

  background:
    #ffffff;

  border:
    1px solid
    #e2e5ea;

  border-radius: 16px;

  box-shadow:
    0 14px 35px
    rgba(
      28,
      38,
      56,
      0.08
    );
}

.results-header {
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding:
    0
    13px;

  color: #8e949e;

  background:
    #fafbfc;

  border-bottom:
    1px solid
    #eceef2;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  text-transform:
    uppercase;

  letter-spacing:
    0.06em;
}

.results-count {
  min-width: 23px;
  height: 23px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding:
    0
    6px;

  color: #4a7cd1;

  background:
    rgba(
      67,
      135,
      238,
      0.08
    );

  border-radius: 8px;
}

.results-list {
  max-height: 290px;

  overflow-y: auto;
}

.results-list::-webkit-scrollbar {
  width: 6px;
}

.results-list::-webkit-scrollbar-thumb {
  background:
    rgba(
      105,
      113,
      127,
      0.24
    );

  border-radius: 999px;
}

/* =========================================================
   COMPANY ITEM
========================================================= */

.company-item {
  width: 100%;

  display: flex;
  align-items: center;

  gap: 11px;

  padding: 11px 12px;

  cursor: pointer;

  text-align: left;

  background:
    transparent;

  border: none;

  border-bottom:
    1px solid
    #f0f1f4;

  transition:
    background-color
    0.18s ease;
}

.company-item:last-child {
  border-bottom: none;
}

.company-item:hover {
  background:
    rgba(
      65,
      132,
      237,
      0.055
    );
}

.logo {
  width: 42px;
  height: 42px;

  font-size: 13px;
}

.info {
  min-width: 0;

  flex: 1;
}

.info .name {
  overflow: hidden;

  color: #30353d;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 12px;

  white-space: nowrap;

  text-overflow:
    ellipsis;
}

.info .spec {
  margin-top: 3px;

  overflow: hidden;

  color: #8d939d;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  white-space: nowrap;

  text-overflow:
    ellipsis;
}

.select-arrow {
  width: 29px;
  height: 29px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #8a929e;

  background:
    #f5f6f8;

  border-radius: 9px;

  transition:
    color
    0.18s ease,
    background-color
    0.18s ease,
    transform
    0.18s ease;
}

.select-arrow svg {
  width: 14px;
  height: 14px;
}

.company-item:hover
.select-arrow {
  color: #4e82dd;

  background:
    rgba(
      66,
      133,
      238,
      0.09
    );

  transform:
    translateX(2px);
}

/* =========================================================
   EMPTY / LOADING
========================================================= */

.no-results,
.loading-state {
  min-height: 92px;

  display: flex;
  align-items: center;

  gap: 12px;

  padding:
    15px;
}

.empty-icon {
  width: 38px;
  height: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #7187ad;

  background:
    #f1f5fb;

  border-radius: 11px;
}

.empty-icon svg {
  width: 18px;
  height: 18px;
}

.no-results strong,
.loading-state strong {
  display: block;

  color: #41464e;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;
}

.no-results span,
.loading-state span {
  display: block;

  margin-top: 3px;

  color: #9298a1;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}

.loader-circle {
  width: 28px;
  height: 28px;

  flex-shrink: 0;

  border:
    3px solid
    rgba(
      67,
      135,
      238,
      0.14
    );

  border-top-color:
    #4387ee;

  border-radius: 50%;

  animation:
    spin
    0.75s
    linear
    infinite;
}

/* =========================================================
   DARK
========================================================= */

html[data-theme="dark"]
.eyebrow {
  color: #777d86;
}

html[data-theme="dark"]
.hint {
  color: #a2a6ae;
}

html[data-theme="dark"]
.input-wrapper {
  background:
    #2b2c31;

  border-color:
    #424349;

  box-shadow:
    inset 0 1px 2px
    rgba(
      0,
      0,
      0,
      0.18
    );
}

html[data-theme="dark"]
.input-wrapper:focus-within {
  background:
    #2f3036;

  border-color:
    #5a8ef0;

  box-shadow:
    0 0 0 3px
    rgba(
      73,
      132,
      239,
      0.14
    );
}

html[data-theme="dark"]
.input-wrapper.active {
  background:
    linear-gradient(
      135deg,
      rgba(
        53,
        132,
        247,
        0.1
      ),
      rgba(
        83,
        104,
        232,
        0.07
      )
    );

  border-color:
    rgba(
      76,
      139,
      238,
      0.3
    );
}

html[data-theme="dark"]
.search-input {
  color: #ffffff;
}

html[data-theme="dark"]
.search-input::placeholder {
  color: #737780;
}

html[data-theme="dark"]
.search-icon {
  color: #818690;
}

html[data-theme="dark"]
.selected-info strong {
  color: #f1f2f4;
}

html[data-theme="dark"]
.selected-info small {
  color: #90949d;
}

html[data-theme="dark"]
.selected-logo,
html[data-theme="dark"]
.logo {
  background:
    linear-gradient(
      135deg,
      #303a4a,
      #333345
    );

  color: #8faeff;
}

html[data-theme="dark"]
.clear-btn {
  color: #a1a6ae;

  background:
    #36373d;
}

html[data-theme="dark"]
.clear-btn:hover {
  color: #ff8d95;

  background:
    rgba(
      216,
      79,
      88,
      0.13
    );
}

html[data-theme="dark"]
.results {
  background:
    #26272c;

  border-color:
    #3a3b41;

  box-shadow:
    0 16px 38px
    rgba(
      0,
      0,
      0,
      0.3
    );
}

html[data-theme="dark"]
.results-header {
  color: #7f848d;

  background:
    #2a2b30;

  border-bottom-color:
    #38393e;
}

html[data-theme="dark"]
.company-item {
  border-bottom-color:
    #35363b;
}

html[data-theme="dark"]
.company-item:hover {
  background:
    rgba(
      67,
      135,
      238,
      0.08
    );
}

html[data-theme="dark"]
.info .name {
  color: #f0f1f3;
}

html[data-theme="dark"]
.info .spec {
  color: #92969e;
}

html[data-theme="dark"]
.select-arrow {
  color: #9499a2;

  background:
    #323339;
}

html[data-theme="dark"]
.company-item:hover
.select-arrow {
  color: #78a6f5;

  background:
    rgba(
      67,
      135,
      238,
      0.12
    );
}

html[data-theme="dark"]
.empty-icon {
  color: #84a9ec;

  background:
    rgba(
      67,
      135,
      238,
      0.1
    );
}

html[data-theme="dark"]
.no-results strong,
html[data-theme="dark"]
.loading-state strong {
  color: #e9eaed;
}

html[data-theme="dark"]
.no-results span,
html[data-theme="dark"]
.loading-state span {
  color: #8d929a;
}

/* =========================================================
   ANIMATION
========================================================= */

.results-enter-active,
.results-leave-active {
  transition:
    opacity
    0.18s ease,
    transform
    0.18s ease;
}

.results-enter-from,
.results-leave-to {
  opacity: 0;

  transform:
    translateY(-5px)
    scale(0.99);
}

@keyframes spin {
  to {
    transform:
      rotate(360deg);
  }
}

/* =========================================================
   MOBILE
========================================================= */

@media (
  max-width: 640px
) {
  .hint {
    font-size: 10px;
  }

  .add-btn {
    padding:
      0
      10px;
  }

  .results-list {
    max-height: 240px;
  }
}

@media (
  prefers-reduced-motion:
  reduce
) {
  .results-enter-active,
  .results-leave-active,
  .input-wrapper,
  .add-btn,
  .clear-btn,
  .company-item,
  .select-arrow {
    transition: none;
  }

  .loader-circle,
  .input-loader {
    animation: none;
  }
}
</style>